import {useMemo} from 'react';

import {QueryKey, useQuery, useQueryClient, UseQueryResult} from '@tanstack/react-query';
import {UseQueryOptions} from '@tanstack/react-query';
import i18next from 'i18next';

import {ApiCustomError} from '../lib/api/ApiCustomError';
import {isApiResponseError} from '../lib/api/v1/types';
import {ApiSerializer} from '../serializers/ApiSerializer';
import {getUserAuthToken} from '../services/auth-service';

interface InvalidateCache {
  invalidateCache: () => void;
}

interface ApiReadArgs<Response> {
  path: string;
  onError?: (error?: any) => void;
  /**
   * Overrides camelCase serializer when necessary
   */
  camelCaseOverride?(data?: any): Response;

  /**
   * Makes queryKey optional, since path is default queryKey value.
   */
  options?: Omit<UseQueryOptions, 'queryKey'> & {queryKey?: QueryKey};
}

const isLocalEnv = process.env.REACT_APP_ENV === 'local';

export const useApiRead = <Response extends object | undefined>({
  path,
  camelCaseOverride,
  options,
}: ApiReadArgs<Response>): UseQueryResult<Response> & InvalidateCache => {
  const queryClient = useQueryClient();
  const token = getUserAuthToken();
  const serialize = useMemo(
    () => ({
      toCamelCase: camelCaseOverride || ApiSerializer.toCamelCase<Response>,
    }),
    [camelCaseOverride]
  );
  const queryKey: QueryKey = options?.queryKey ? options.queryKey : [path];

  const response = useQuery({
    queryKey,
    select: (data) => {
      return serialize.toCamelCase(data) as Response;
    },
    queryFn: async () => {
      const headers: HeadersInit = {
        'X-Application-Id': process.env.REACT_APP_PROJECT_ID ?? '',
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept-Language': i18next.language,
      };

      if (isLocalEnv && process.env.REACT_APP_API_KEY) {
        headers['X-Application-Secret'] = process.env.REACT_APP_API_KEY;
      }

      const response = await fetch(`${process.env.REACT_APP_API_HOST}${path}`, {
        method: 'GET',
        headers,
      });

      const data = await response.json();

      if (!response.ok) {
        if (isApiResponseError(data)) {
          throw new ApiCustomError(data);
        } else {
          throw new Error('Could not connect to API, or unexpected response.', data);
        }
      }
      return data as Response;
    },
    ...options,
  }) as UseQueryResult<Response>;

  return {
    ...response,
    invalidateCache: () => queryClient.invalidateQueries({queryKey}),
  };
};
